import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Users,
  ShoppingCart,
  DollarSign,
  Globe,
  ChevronRight,
  Facebook,
  Instagram,
  Music2,
  LayoutDashboard,
  User as UserIcon,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCartCount } from "@/lib/cart";
import SignupOptionsSection from "@/components/public/SignupOptionsSection";
import PageMeta from "@/components/PageMeta";

const API = process.env.REACT_APP_BACKEND_URL;

// Uploaded video for the Lifted Ecosystem storytelling section
const LIFTED_WORLD_VIDEO =
  "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/9bdsdg94_Lifted%20world.MP4";

// Uploaded assets from user (Job ID: lifted-core / artifacts)
const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/892vnebi_instories_4BF083B0-5698-4A7C-A701-08EFA348C7CC.mov";
// NOTE: The user-uploaded still image (3gw86bs1...) is the full reference
// homepage design, not a clean astronaut/space still. Using it as a fallback
// would render the reference image as the page background. Use a CSS-only
// cosmic gradient instead until a clean still is provided.

const LATM_LOGO = "/latm-logo.png"; // existing brand asset path

// ───────────────────────────────────────────────────────────────────────────
// HEADER
// ───────────────────────────────────────────────────────────────────────────
const PRIVATE_DASHBOARD_ROLES = new Set([
  "affiliate", "driver", "partner", "franchisee", "trainer",
  "teacher", "affiliate_trainer", "franchisee_trainer", "training_admin",
  "admin", "master_admin",
]);

function HomeHeader() {
  const { user } = useAuth();
  // Phase 22 — live cart badge (reactive, no refresh required)
  const cartCount = useCartCount();
  const cartBadge = cartCount > 0 ? (cartCount > 99 ? "99+" : String(cartCount)) : null;
  const navItems = [
    { label: "Shop", to: "/shop" },
    { label: "Monthly Box", to: "/lifted-monthly-box" },
    { label: "Marketing", to: "/lifted-marketing" },
    { label: "News", to: "/lifted-views-news" },
    { label: "Ecosystem", to: "/ecosystem" },
    { label: "Training", to: "/training" },
    { label: "Supplies", to: "/supplies" },
    { label: "Affiliate", to: "/affiliate-zone" },
  ];

  const isAuthenticated = user && typeof user === "object";
  const showDashboard =
    isAuthenticated &&
    (user.roles || []).some((r) => PRIVATE_DASHBOARD_ROLES.has(r));

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-black/45 border-b border-white/5"
      data-testid="home-header"
    >
      <div className="container mx-auto px-4 py-3 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="home-logo">
          <img
            src={LATM_LOGO}
            alt="Lifted at the Moment"
            className="h-9 w-auto"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <span className="font-anton text-sm uppercase tracking-wider text-white hidden md:inline">
            Lifted<span className="text-lifted-green">.</span>com
          </span>
        </Link>
        {/* Phase 22 — desktop nav bumped from text-xs → text-base (Step 1).
            Spacing tightened to gap-6 to keep all items on one line. */}
        <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {navItems.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="font-bebas tracking-[0.18em] text-base uppercase text-white/85 hover:text-lifted-green transition-colors"
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {/* Phase 22 — live cart badge on homepage too (Step 2). Updates
              instantly via `cart:updated` CustomEvent. */}
          <Link
            to="/cart"
            className="relative px-3 py-2 text-xs uppercase tracking-wider border border-white/20 rounded text-white hover:bg-white/5 inline-flex items-center gap-1.5"
            data-testid="home-header-cart"
            aria-label="Cart"
          >
            <ShoppingBag className="h-3 w-3" />
            <span className="hidden sm:inline">Cart</span>
            {cartBadge && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border border-black"
                data-testid="home-header-cart-badge"
              >
                {cartBadge}
              </span>
            )}
          </Link>
          {isAuthenticated ? (
            <>
              {showDashboard && (
                <Link
                  to="/dashboard"
                  className="px-3 py-2 text-xs uppercase tracking-wider border border-white/20 rounded text-white hover:bg-white/5 inline-flex items-center gap-1.5"
                  data-testid="header-dashboard"
                >
                  <LayoutDashboard className="h-3 w-3" /> Dashboard
                </Link>
              )}
              <Link
                to="/account"
                className="px-3 py-2 text-xs uppercase tracking-wider bg-lifted-green text-black rounded font-bebas hover:bg-lifted-green/90 inline-flex items-center gap-1.5"
                data-testid="header-account"
              >
                <UserIcon className="h-3 w-3" /> Account
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                className="px-4 py-2 text-xs uppercase tracking-wider border border-white/20 rounded text-white hover:bg-white/5"
                data-testid="header-login"
              >
                Login
              </Link>
              <Link
                to="/affiliate-zone"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-lifted-green text-black rounded font-bebas hover:bg-lifted-green/90"
                data-testid="header-join-ecosystem"
              >
                Join the Ecosystem
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// HERO
// ───────────────────────────────────────────────────────────────────────────
function HeroSection() {
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Try to autoplay; some browsers reject muted autoplay too on .mov — fallback to still
    v.play().catch(() => setVideoFailed(true));
  }, []);

  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      {/* Background — video with CSS gradient fallback (no still image to avoid
          showing the reference design as background). */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at 60% 35%, rgba(28,86,40,0.35) 0%, rgba(4,8,12,0.95) 55%, #04070a 100%)",
        }}
      >
        {!videoFailed && (
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setVideoFailed(true)}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src={HERO_VIDEO} type="video/quicktime" />
          </video>
        )}
        {/* readability overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/85" />
      </div>

      <div className="relative z-10 container mx-auto px-4 pt-16 pb-24 lg:pt-24 lg:pb-32 min-h-[80vh] grid lg:grid-cols-2 gap-8">
        <div className="max-w-xl">
          <h1
            className="font-anton uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-white"
            data-testid="home-hero-headline"
          >
            The All-in-One<br />
            <span className="text-lifted-green">Lifted Ecosystem</span>
          </h1>
          <p className="font-bebas tracking-wider text-xl text-white mt-6 leading-snug">
            Education. Community. Opportunity. Supplies.
            <br />
            <span className="text-lifted-green">All In One Place.</span>
          </p>
          <p className="text-white/80 mt-6 max-w-md">
            Lifted At The Moment is more than a brand — it is a connected ecosystem
            built around growth, ecommerce, training, delivery, networking, and
            opportunity.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-row">
            <Link
              to="/shop"
              className="px-7 py-3 font-bebas uppercase tracking-wider bg-lifted-green text-black rounded inline-flex items-center justify-center gap-2 hover:bg-lifted-green/90"
              data-testid="hero-cta-shop"
            >
              Shop Lifted
              <ChevronRight size={18} />
            </Link>
            <Link
              to="/affiliate-zone"
              className="px-7 py-3 font-bebas uppercase tracking-wider border border-lifted-green/60 text-lifted-green rounded inline-flex items-center justify-center gap-2 hover:bg-lifted-green/10"
              data-testid="hero-cta-join"
            >
              Join the Movement
              <ChevronRight size={18} />
            </Link>
            <Link
              to="/ecosystem"
              className="px-7 py-3 font-bebas uppercase tracking-wider border border-white/30 text-white rounded inline-flex items-center justify-center gap-2 hover:bg-white/5"
              data-testid="hero-cta-ecosystem"
            >
              Explore the Ecosystem
            </Link>
          </div>
          <p className="text-xs text-white/55 mt-4 italic">
            Explore the ecosystem before choosing your path.
          </p>
        </div>

        {/* Right side accent — "Abducted by the Vision" */}
        <div className="hidden lg:flex items-start justify-end pt-12">
          <div
            className="text-right select-none"
            style={{ transform: "rotate(8deg)" }}
            data-testid="hero-accent"
          >
            <p className="font-anton text-3xl text-lifted-green leading-tight tracking-tight">
              ABDUCTED
              <br />
              BY THE
              <br />
              <span className="text-white">VISION</span>
            </p>
          </div>
        </div>
      </div>

      {/* Tagline band */}
      <div className="relative z-10 border-y border-white/10 bg-black/60 backdrop-blur">
        <div className="container mx-auto px-4 py-5 text-center">
          <p
            className="font-bebas tracking-[0.3em] uppercase text-sm sm:text-base text-white"
            data-testid="hero-tagline"
          >
            One Movement. One Mission.{" "}
            <span className="text-lifted-green">We Are Lifted.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// 5 ROLE CARDS
// ───────────────────────────────────────────────────────────────────────────
const ROLE_CARDS = [
  {
    icon: GraduationCap,
    title: "Training",
    blurb:
      "Step-by-step training, coaching, and resources to help you learn, grow, and succeed.",
    cta: "Learn more",
    to: "/training",
    testId: "role-card-training",
  },
  {
    icon: Users,
    title: "The Ecosystem",
    blurb:
      "Your all-in-one dashboard to manage your business, track earnings, and access everything Lifted has to offer.",
    cta: "Learn more",
    to: "/ecosystem",
    testId: "role-card-ecosystem",
  },
  {
    icon: ShoppingCart,
    title: "Supplies",
    blurb:
      "Premium accessories, packaging, and same-day delivery — built on the Lifted network.",
    cta: "Shop now",
    to: "/same-day",
    testId: "role-card-supplies",
  },
  {
    icon: DollarSign,
    title: "Affiliate",
    blurb:
      "The Lifted affiliate program. Earn commissions, bonuses, and build a lasting income.",
    cta: "Learn more",
    to: "/affiliate-zone",
    testId: "role-card-affiliate",
  },
  {
    icon: Globe,
    title: "Access",
    blurb:
      "Unlock your dashboard, tools, resources, and a global community of like-minded operators.",
    cta: "Get access",
    to: "/auth/login",
    testId: "role-card-access",
  },
];

function RoleCardsSection() {
  return (
    <section
      className="border-y border-white/5 bg-black/30 backdrop-blur"
      data-testid="home-role-cards"
    >
      <div className="container mx-auto px-4 py-12 lg:py-16 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {ROLE_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.title}
              to={c.to}
              data-testid={c.testId}
              className="group block rounded-xl border border-lifted-green/15 bg-black/40 p-5 hover:border-lifted-green/50 hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-lifted-green/15 flex items-center justify-center text-lifted-green mb-4 group-hover:bg-lifted-green/30 transition-colors">
                <Icon size={20} />
              </div>
              <h3 className="font-bebas uppercase tracking-wider text-white text-lg">
                {c.title}
              </h3>
              <p className="text-xs text-white/65 mt-2 mb-4 leading-snug">{c.blurb}</p>
              <span className="text-[10px] uppercase tracking-[0.2em] text-lifted-green flex items-center gap-1">
                {c.cta} <ChevronRight size={12} />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MARKETING + NEWS — two image-led feature cards above Hot Products.
// Added in Phase "Add Lifted Monthly Box / Affiliate Box, Lifted Marketing,
// and Lifted Views News" per owner directive. Side-by-side on desktop,
// stacked on mobile. Matches the attached homepage blueprint.
// ───────────────────────────────────────────────────────────────────────────
function MarketingAndNewsCardsSection() {
  const cards = [
    {
      key: "marketing",
      title: "Lifted Marketing",
      copy: "Businesses, creators, affiliates, and social promotion — grow your brand and get seen.",
      to: "/lifted-marketing",
      cta: "Explore Marketing",
      img: "/images/covers/lifted-marketing-sm.webp",
      alt: "Lifted Marketing cover — creator connected to social platforms around the globe",
    },
    {
      key: "news",
      title: "Lifted Views News",
      copy: "Cannabis. CBD. Vapes. State policy.",
      to: "/lifted-views-news",
      cta: "Read the News",
      img: "/images/covers/lifted-views-news-sm.webp",
      alt: "Lifted Views News cover — cannabis leaf, Earth, and a Lifted News newspaper",
    },
  ];
  return (
    <section className="py-10 lg:py-14" data-testid="home-marketing-news">
      <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
        {cards.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`home-card-${c.key}`}
            className="group relative overflow-hidden rounded-2xl border border-lifted-green/20 bg-black flex flex-col hover:border-lifted-green/60 hover:-translate-y-1 transition-[transform,border-color] duration-200"
          >
            <img src={c.img} alt={c.alt} loading="lazy" width="836" height="470"
              className="w-full h-auto aspect-[16/9] object-cover group-hover:scale-[1.02] transition-transform duration-500"
              data-testid={`home-card-${c.key}-image`} />
            <div className="p-5 lg:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <h3 className="sr-only">{c.title}</h3>
              <p className="text-white/75 text-sm max-w-md">{c.copy}</p>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-lifted-green text-black font-bebas tracking-[0.18em] uppercase text-xs w-fit shrink-0 group-hover:bg-white transition-colors"
                    data-testid={`home-card-${c.key}-cta`}>
                {c.cta} <ChevronRight size={14} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TOP PRODUCTS — pulled from internal Lifted inventory catalog
// (Lifted Core is the System of Record — NO live Shopify dependency)
// Phase 22 — Renamed from "New Products of the Week" to "Top Products" with
// a never-empty fallback chain: featured → best_sellers → new_releases →
// gear/accessories.
// ───────────────────────────────────────────────────────────────────────────
function TopProductsSection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      // Phase 23+ — Try the featured bundle first. If it fails or returns
      // empty, fall back to the full active catalog so the homepage rail
      // is NEVER empty when products exist.
      let chain = [];
      try {
        const r = await fetch(`${API}/api/shop/featured`);
        if (r.ok) {
          const data = await r.json();
          chain = [
            ...(data.top_products || []),
            ...(data.featured || []),
            ...(data.best_sellers || []),
            ...(data.new_releases || []),
            ...(data.seasonal || []),
            ...(data.gear || []),
            ...(data.accessories || []),
            ...(data.accessories_preview || []),
          ];
        }
      } catch (e1) {
        console.warn("featured bundle failed, falling back to /api/shop/products", e1);
      }
      // Layer 2 fallback — direct active catalog query
      if (chain.length === 0) {
        try {
          const r2 = await fetch(`${API}/api/shop/products?limit=20`);
          if (r2.ok) {
            const data2 = await r2.json();
            chain = data2.products || data2 || [];
          }
        } catch (e2) {
          console.warn("catalog fallback also failed", e2);
        }
      }
      if (chain.length === 0) {
        throw new Error("All product endpoints returned empty.");
      }
      // Dedupe
      const seen = new Set();
      const unique = [];
      for (const p of chain) {
        const k = `${p.id || ""}|${p.slug || ""}|${p.handle || ""}|${p.title || ""}`;
        if (!seen.has(k)) { seen.add(k); unique.push(p); }
        if (unique.length >= 10) break;
      }
      setProducts(unique);
    } catch (e) {
      setError(String(e?.message || e));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="py-14 lg:py-20" data-testid="home-top-products">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-anton uppercase text-3xl sm:text-4xl lg:text-5xl text-white">
            <span className="italic text-lifted-green">Top</span> Products
          </h2>
          <p className="text-white/65 mt-2 text-sm">
            Featured gear, top sellers, and the latest drops from the Lifted catalog.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-white/50" data-testid="top-products-loading">
            Loading top products…
          </p>
        ) : error ? (
          <div className="text-center text-rose-300 py-6 space-y-2"
               data-testid="top-products-error">
            <p className="text-sm">Couldn't load top products: <code>{error}</code></p>
            <button onClick={load}
                    className="px-4 py-2 rounded bg-lifted-green text-black text-xs font-bebas tracking-[0.18em] uppercase"
                    data-testid="top-products-retry">
              Retry
            </button>
          </div>
        ) : products.length === 0 ? (
          <div
            className="text-center text-white/60 py-10"
            data-testid="home-top-products-empty"
          >
            <p className="font-bebas tracking-wider uppercase text-lifted-green text-lg">
              Catalog is being refreshed
            </p>
            <p className="text-sm mt-2">
              Our team is curating the next release.{" "}
              <Link to="/shop" className="text-lifted-green underline">
                Browse the full shop
              </Link>
              .
            </p>
          </div>
        ) : (
          <div
            className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-5 lg:gap-5 lg:overflow-visible lg:snap-none"
            data-testid="top-products-rail"
          >
            {products.map((p) => {
              const slug = p.slug || p.handle || p.id;
              return (
              <Link
                key={p.id || slug}
                to={`/products/${slug}`}
                data-testid={`top-product-${p.id || slug}`}
                className="block snap-start shrink-0 w-[70vw] sm:w-[42vw] lg:w-auto rounded-xl border border-lifted-green/15 bg-black/40 overflow-hidden hover:border-lifted-green/45 transition-colors group focus:outline-none focus:ring-2 focus:ring-lifted-green/60"
              >
                <article>
                  <div className="aspect-square bg-black overflow-hidden flex items-center justify-center">
                    {((p.media && p.media[0]) || p.image) ? (
                      <img
                        src={(p.media && p.media[0]) || p.image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-xs text-lifted-dim/60 uppercase tracking-[0.18em] font-bebas">
                        Image coming
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-bebas tracking-wider text-white text-sm uppercase leading-tight line-clamp-2 min-h-[2.5em]">
                      {p.title}
                    </h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-lifted-green text-sm">
                        {typeof p.price === "number"
                          ? `$${p.price.toFixed(2)}`
                          : p.price || "—"}
                      </span>
                      <span
                        className="text-[10px] uppercase tracking-wider text-white/70 group-hover:text-lifted-green"
                        data-testid={`view-product-${p.id || slug}`}
                      >
                        View →
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            );})}
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-6 py-3 font-bebas uppercase tracking-wider bg-lifted-green text-black rounded hover:bg-lifted-green/90"
            data-testid="shop-all-top-products-btn"
          >
            Shop the full catalog <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// POWER THE LIFTED ECOSYSTEM — affiliate conversion engine
// Replaces the old "Join a Global Community" section. Centerpiece is the
// uploaded "Lifted world" video; surrounding glass cards tell the ecosystem
// growth story: Affiliate → Awareness → Customers/Drivers/Partners/Franchise
// → Community Growth.
// ───────────────────────────────────────────────────────────────────────────
function EcosystemPowerSection() {
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => setVideoFailed(true));
  }, []);

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPaused(false)).catch(() => {});
    } else {
      v.pause();
      setPaused(true);
    }
  }

  const flowSteps = [
    { n: 1, label: "Customer", note: "Discovers Lifted." },
    { n: 2, label: "Affiliate", note: "Shares the brand and ecosystem." },
    { n: 3, label: "Awareness", note: "Reaches new audiences organically." },
    { n: 4, label: "Drivers", note: "Apply to power Lifted Supplies." },
    { n: 5, label: "Partner Stores", note: "Plug into the local network." },
    { n: 6, label: "Franchise", note: "Operators open new territories." },
    { n: 7, label: "Growth", note: "The ecosystem expands." },
  ];

  const trustPoints = [
    "One account across the ecosystem",
    "Training included",
    "Community focused",
    "Role-based growth paths",
    "Affiliate tools available",
    "Partner & franchise opportunities",
  ];

  return (
    <section
      className="relative py-16 lg:py-24 overflow-hidden border-y border-lifted-green/15"
      data-testid="home-ecosystem-power"
    >
      {/* Deep-space cosmic background — gradient + nebula glow + stars */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 30% 30%, rgba(28,86,40,0.45) 0%, rgba(4,8,12,0) 60%)," +
            "radial-gradient(ellipse 50% 70% at 80% 70%, rgba(86,40,180,0.35) 0%, rgba(4,8,12,0) 60%)," +
            "radial-gradient(ellipse at 50% 50%, #06120b 0%, #04070a 65%, #02050a 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 pointer-events-none opacity-50 motion-safe:animate-[pulse_8s_ease-in-out_infinite]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 22%, rgba(255,255,255,0.6) 0.5px, transparent 1px)," +
            "radial-gradient(circle at 28% 64%, rgba(255,255,255,0.5) 0.5px, transparent 1px)," +
            "radial-gradient(circle at 47% 18%, rgba(255,255,255,0.45) 0.5px, transparent 1px)," +
            "radial-gradient(circle at 63% 73%, rgba(255,255,255,0.4) 0.5px, transparent 1px)," +
            "radial-gradient(circle at 82% 30%, rgba(255,255,255,0.5) 0.5px, transparent 1px)," +
            "radial-gradient(circle at 91% 86%, rgba(255,255,255,0.55) 0.5px, transparent 1px)",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Title + lede */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-lifted-green mb-3">
            The People Behind The Movement
          </div>
          <h2
            className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]"
            data-testid="ecosystem-power-headline"
          >
            Power the <span className="text-lifted-green">Lifted</span><br />
            Ecosystem.
          </h2>
          <p className="text-white/80 mt-5 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Lifted grows because real people share it. Customers discover the brand,
            affiliates create awareness, and that awareness brings drivers, partner
            stores, and franchise operators into the network. The ecosystem expands
            with the people who power it.
          </p>
        </div>

        {/* 2-column: video centerpiece (left/center) + ecosystem flow (right) */}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          {/* Video centerpiece */}
          <div
            className="relative rounded-2xl overflow-hidden border border-lifted-green/20 shadow-[0_0_60px_-20px_rgba(28,200,90,0.35)] aspect-video bg-black"
            data-testid="lifted-world-video-wrap"
          >
            {!videoFailed && (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onClick={togglePlay}
                onError={() => setVideoFailed(true)}
                aria-label="Lifted ecosystem — global growth"
              >
                <source src={LIFTED_WORLD_VIDEO} type="video/mp4" />
              </video>
            )}
            {videoFailed && (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-[radial-gradient(ellipse_at_center,rgba(28,86,40,0.5),rgba(4,8,12,1))]">
                <Globe size={56} className="text-lifted-green mb-3" />
                <p className="font-anton uppercase text-2xl text-white tracking-wide">
                  Lifted World
                </p>
                <p className="text-xs text-white/55 mt-1">
                  Video unavailable in this browser
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={togglePlay}
              className="absolute bottom-3 right-3 text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-black/65 text-white border border-white/15 hover:bg-black/85"
              data-testid="lifted-world-toggle"
            >
              {paused ? "Play" : "Pause"}
            </button>
            <div className="absolute top-3 left-3 px-3 py-1 text-[10px] uppercase tracking-[0.3em] bg-black/65 text-lifted-green border border-lifted-green/30 rounded-full">
              Lifted World · Live
            </div>
          </div>

          {/* Flow story */}
          <div data-testid="ecosystem-flow-story">
            <p className="font-bebas tracking-[0.2em] text-lifted-green uppercase text-xs mb-3">
              How the ecosystem grows
            </p>
            <ol className="space-y-2">
              {flowSteps.map((s) => (
                <li
                  key={s.n}
                  data-testid={`flow-step-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-lifted-green/15 bg-black/45 backdrop-blur"
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-lifted-green/20 text-lifted-green border border-lifted-green/40 flex items-center justify-center text-xs font-mono">
                    {s.n}
                  </span>
                  <div className="min-w-0">
                    <p className="font-bebas tracking-wider uppercase text-sm text-white">
                      {s.label}
                    </p>
                    <p className="text-xs text-white/65 leading-snug">{s.note}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Trust strip */}
        <div
          className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
          data-testid="ecosystem-trust-strip"
        >
          {trustPoints.map((t) => (
            <div
              key={t}
              className="flex items-center gap-2 p-3 rounded-lg border border-lifted-green/15 bg-black/45 backdrop-blur"
            >
              <ChevronRight size={14} className="text-lifted-green shrink-0" />
              <span className="font-bebas tracking-wider uppercase text-[10.5px] text-white leading-tight">
                {t}
              </span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/affiliate-zone"
            className="px-8 py-3.5 font-bebas uppercase tracking-wider bg-lifted-green text-black rounded inline-flex items-center justify-center gap-2 hover:bg-lifted-green/90"
            data-testid="ecosystem-power-cta-primary"
          >
            Become an Affiliate <ChevronRight size={18} />
          </Link>
          <Link
            to="/ecosystem"
            className="px-8 py-3.5 font-bebas uppercase tracking-wider border border-white/30 text-white rounded inline-flex items-center justify-center gap-2 hover:bg-white/5"
            data-testid="ecosystem-power-cta-secondary"
          >
            Explore the Ecosystem
          </Link>
        </div>

        {/* Compliance footnote */}
        <p className="text-[11px] text-white/45 text-center mt-4 max-w-xl mx-auto leading-relaxed">
          Approval required for driver, partner store, and franchise paths. No
          earnings or income promises — affiliates earn based on real activity.
        </p>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// FINAL CTA
// ───────────────────────────────────────────────────────────────────────────
function FinalCtaSection() {
  return (
    <section className="py-16 lg:py-24" data-testid="home-final-cta">
      <div className="container mx-auto px-4">
        <div className="rounded-2xl border border-lifted-green/20 bg-black/50 p-8 lg:p-14 text-center max-w-4xl mx-auto">
          <h2 className="font-anton uppercase text-3xl sm:text-4xl lg:text-5xl text-white">
            Ready to <span className="text-lifted-green">Get Lifted?</span>
          </h2>
          <p className="text-white/75 mt-3 max-w-xl mx-auto">
            Your future is waiting. Explore the ecosystem, choose your path, and
            become part of the Lifted movement.
          </p>
          <Link
            to="/affiliate-zone"
            className="mt-7 inline-flex items-center gap-2 px-8 py-4 font-bebas uppercase tracking-wider bg-lifted-green text-black rounded hover:bg-lifted-green/90"
            data-testid="final-cta-join"
          >
            Join the Ecosystem
            <ChevronRight size={18} />
          </Link>
          <p className="text-xs text-white/45 mt-2 uppercase tracking-wider">
            Free to get started
          </p>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// FOOTER
// ───────────────────────────────────────────────────────────────────────────
const FOOTER_COLS = [
  {
    title: "Ecosystem",
    links: [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Community", to: "/ecosystem" },
      { label: "Events", to: "/ecosystem#events" },
      { label: "Marketing", to: "/lifted-marketing" },
      { label: "Support", to: "/contact" },
    ],
  },
  {
    title: "Discover",
    links: [
      { label: "Lifted Marketing", to: "/lifted-marketing" },
      { label: "Lifted Views News", to: "/lifted-views-news" },
      { label: "Monthly Box", to: "/lifted-monthly-box" },
      { label: "Affiliate Zone", to: "/affiliate-zone" },
      { label: "Ecosystem", to: "/ecosystem" },
    ],
  },
  {
    title: "Training",
    links: [
      { label: "Getting started", to: "/training" },
      { label: "Courses", to: "/training/modules" },
      { label: "Resources", to: "/training" },
      { label: "Webinars", to: "/training" },
      { label: "Certifications", to: "/certification" },
    ],
  },
  {
    title: "Supplies",
    links: [
      { label: "Shop all", to: "/shop" },
      { label: "Accessories", to: "/collections/lifted-accessories" },
      { label: "Gear", to: "/collections/lifted-gear" },
      { label: "Monthly Box", to: "/lifted-monthly-box" },
      { label: "Same-day", to: "/same-day" },
    ],
  },
  {
    title: "Affiliate",
    links: [
      { label: "Overview", to: "/affiliate-zone" },
      { label: "Commissions", to: "/affiliate-zone#commissions" },
      { label: "Bonuses", to: "/affiliate-zone" },
      { label: "Promotional tools", to: "/affiliate-zone" },
      { label: "Leaderboard", to: "/affiliate-zone" },
    ],
  },
];

function HomeFooter() {
  return (
    <footer className="border-t border-lifted-green/15 bg-black/85 mt-4" data-testid="home-footer">
      <div className="container mx-auto px-4 py-12 grid lg:grid-cols-6 gap-8">
        <div className="lg:col-span-2">
          <img
            src={LATM_LOGO}
            alt="Lifted at the Moment"
            className="h-10 w-auto mb-3"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <p className="text-xs text-white/55 leading-relaxed max-w-xs">
            We educate, equip, and elevate operators across the Lifted ecosystem —
            ecommerce, training, delivery, networking, and opportunity.
          </p>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://facebook.com/liftedatthemoment"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-lifted-green/30 flex items-center justify-center text-lifted-green hover:bg-lifted-green/10"
              data-testid="footer-social-facebook"
            >
              <Facebook size={14} />
            </a>
            <a
              href="https://instagram.com/liftedatthemoment"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-lifted-green/30 flex items-center justify-center text-lifted-green hover:bg-lifted-green/10"
              data-testid="footer-social-instagram"
            >
              <Instagram size={14} />
            </a>
            <a
              href="https://tiktok.com/@liftedatthemoment"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-lifted-green/30 flex items-center justify-center text-lifted-green hover:bg-lifted-green/10"
              data-testid="footer-social-tiktok"
            >
              <Music2 size={14} />
            </a>
          </div>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.title}>
            <h4 className="font-bebas tracking-[0.2em] uppercase text-xs text-lifted-green mb-3">
              {col.title}
            </h4>
            <ul className="space-y-1.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-xs text-white/70 hover:text-white"
                    data-testid={`footer-link-${col.title.toLowerCase()}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/5">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.25em] text-white/45">
          <span>We are lifted.</span>
          <span>One movement. One mission.</span>
          <span>© 2026 Lifted at the Moment. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// PAGE
// ───────────────────────────────────────────────────────────────────────────
export default function HomeNew() {
  return (
    <div className="min-h-screen bg-[#04070a] text-white" data-testid="home-new-page">
      <PageMeta path="/" ogImage="/og-image.png" />
      <HomeHeader />
      <main>
        <HeroSection />
        <RoleCardsSection />
        <MarketingAndNewsCardsSection />
        <TopProductsSection />
        <EcosystemPowerSection />
        {/* Phase 22 — Public signup entry points */}
        <SignupOptionsSection testid="home-signup-options" />
        <FinalCtaSection />
      </main>
      <HomeFooter />
    </div>
  );
}
